/**
 * Route Prefetching Hook
 *
 * Enables intelligent prefetching of route chunks using Intersection Observer.
 * When a navigation link comes into view, its associated route chunk is automatically
 * prefetched in the background, improving perceived navigation speed.
 *
 * @example
 * // In a navigation menu component
 * function NavigationMenu() {
 *   const prefetch = useRoutePreload();
 *
 *   return (
 *     <nav>
 *       <Link
 *         to="/leaderboard"
 *         ref={(el) => el && prefetch(el, '/leaderboard')}
 *       >
 *         Leaderboard
 *       </Link>
 *       <Link
 *         to="/fundraising"
 *         ref={(el) => el && prefetch(el, '/fundraising')}
 *       >
 *         Fundraising
 *       </Link>
 *     </nav>
 *   );
 * }
 */

import { useCallback, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';

/**
 * Configuration for route prefetch behavior
 */
interface PrefetchConfig {
  /** Delay before prefetching starts (ms) - default: 500 */
  delay?: number;
  /** Root margin for intersection observer (px) - default: '0px' */
  rootMargin?: string;
  /** Threshold for intersection - default: 0 */
  threshold?: number | number[];
}

/**
 * Prefetch tracking to avoid duplicate requests
 */
const prefetchedRoutes = new Set<string>();

/**
 * Custom hook for intelligent route chunk prefetching via Intersection Observer
 *
 * Automatically prefetches lazy-loaded route chunks when their navigation links
 * become visible in the viewport. Uses requestIdleCallback for non-blocking prefetch.
 *
 * @param config - Optional prefetch configuration
 * @returns prefetch function to attach to navigation links
 *
 * @example
 * const prefetch = useRoutePreload({ delay: 300, rootMargin: '50px' });
 * <Link ref={(el) => el && prefetch(el, '/dashboard')} to="/dashboard">
 */
export function useRoutePreload(config: PrefetchConfig = {}) {
  const {
    delay = 500,
    rootMargin = '0px',
    threshold = 0,
  } = config;

  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Simulate route chunk prefetch
   * In real apps, this would trigger webpack's __webpack_require__.e() for dynamic imports
   * With React.lazy(), the chunk is automatically requested when needed
   * This function serves as a hook point for custom prefetch logic
   */
  const prefetchRoute = useCallback((routePath: string) => {
    // Skip if already prefetched
    if (prefetchedRoutes.has(routePath)) {
      return;
    }

    // Schedule prefetch during browser idle time
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          logger.debug(`Route prefetch initiated: ${routePath}`);
          prefetchedRoutes.add(routePath);
        },
        { timeout: 2000 }
      );
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        logger.debug(`Route prefetch initiated (fallback): ${routePath}`);
        prefetchedRoutes.add(routePath);
      }, 100);
    }
  }, []);

  /**
   * Attach to navigation link element for automatic prefetch on visibility
   * @param element - The navigation link element
   * @param routePath - The route path to prefetch
   */
  const prefetch = useCallback(
    (element: HTMLElement, routePath: string) => {
      if (!element) return;

      // Create observer if not exists
      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const route = (entry.target as HTMLElement).dataset.prefetchRoute;
                if (route && !prefetchedRoutes.has(route)) {
                  // Delay prefetch to avoid blocking immediate interactions
                  if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                  }
                  timeoutRef.current = setTimeout(() => {
                    prefetchRoute(route);
                  }, delay);
                }
              }
            });
          },
          {
            rootMargin,
            threshold,
          }
        );
      }

      // Store route path on element for observer callback
      element.dataset.prefetchRoute = routePath;

      // Observe this element
      observerRef.current.observe(element);

      // Return cleanup function
      return () => {
        if (observerRef.current && element) {
          observerRef.current.unobserve(element);
        }
      };
    },
    [prefetchRoute, delay, rootMargin, threshold]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return prefetch;
}

/**
 * Higher-level hook for prefetching multiple routes at once
 *
 * @example
 * const prefetchBatch = useBatchRoutePreload([
 *   { selector: 'a[href="/leaderboard"]', route: '/leaderboard' },
 *   { selector: 'a[href="/fundraising"]', route: '/fundraising' },
 * ]);
 *
 * // In useEffect:
 * // prefetchBatch();
 */
export function useBatchRoutePreload(
  routes: Array<{ selector: string; route: string }>,
  config?: PrefetchConfig
) {
  const prefetch = useRoutePreload(config);

  return useCallback(() => {
    routes.forEach(({ selector, route }) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => {
        prefetch(el as HTMLElement, route);
      });
    });
  }, [routes, prefetch]);
}

/**
 * Manually clear prefetch cache (useful for testing or resetting)
 */
export function clearPrefetchCache() {
  prefetchedRoutes.clear();
  logger.debug('Prefetch cache cleared');
}

/**
 * Get list of currently prefetched routes (for debugging/monitoring)
 */
export function getPrefetchedRoutes(): string[] {
  return Array.from(prefetchedRoutes);
}
